import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { differenceInDays, startOfWeek, subDays } from 'date-fns'
import { useState } from 'react'
import z from 'zod'
import { DrilldownButton } from '~/components/button'
import { DateRangePicker } from '~/components/date-picker'
import { Avatar } from '~/features/avatar'
import {
  ChannelsCommandList,
  CommandPopover,
  ScorecardsCommandList,
} from '~/features/command-lists'
import { formatDuration } from '~/helpers/dateHelper'
import { useTimespanPreference, useTitle, useWeekStartsOn } from '~/hooks'
import { useAnalytics } from '~/modules/analytics/hooks/useAnalytics'
import { useTRPC } from '~/services/trpc'

import { FilterCollection } from '@hap/analytics'
import { Icon, cn } from '@hap/components'
import { AVERAGE_HANDLE_TIME_THRESHOLD, DEAD_AIR_THRESHOLD_PHONE } from '@hap/constants'
import { Timespan } from '@hap/utils'
import { commafy, toPercent } from '@hap/utils'

import { useAnalyticsPresets } from '../indicators/-components/hooks'
import { FilterOpIn } from '../indicators/-components/metric-widget'
import { EvaluationCriteriaInsights } from './-components/evaluation-criteria-insights'
import {
  BaseReportProps,
  GenericInteractionBreakdown,
} from './-components/generic/generic-interaction-breakdown'
import {
  DeadAirHeading,
  HandleTimeHeading,
  OvertalkHeading,
  calculateOvertalkScore,
} from './-components/generic/generic-interaction-heading'
import { GenericInteractionTrends } from './-components/generic/generic-interaction-trends'
import { IndicatorTab } from './-components/indicator-tab'
import { EvaluationScoreTrends } from './-components/scorecard-trends'
import { SentimentBreakdown } from './-components/sentiment/sentiment-breakdown'
import { SentimentHeading } from './-components/sentiment/sentiment-heading'
import { SentimentTrends } from './-components/sentiment/sentiment-trends'

const metricFilters = z.object({
  channelIds: FilterOpIn.optional(),
  scorecardIds: FilterOpIn.optional(),
})

type MetricFilters = z.infer<typeof metricFilters>

export const searchParamsSchema = z.object({
  filters: metricFilters.default({}),
  timespan: Timespan.Info.optional(),
})

type SearchParams = z.infer<typeof searchParamsSchema>

export const Route = createFileRoute('/_authenticated/$alias/_app/members/$memberId')({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
})

function RouteComponent() {
  const trpc = useTRPC()
  const { filters, timespan: selectedTimespan } = Route.useSearch()
  const weekStartsOn = useWeekStartsOn()
  const { timespan, setTimespanPreference } = useTimespanPreference({
    selectedTimespan,
    startOffset: (date) => startOfWeek(subDays(date, 30), { weekStartsOn }),
  })
  const navigate = Route.useNavigate()

  const { memberId } = Route.useParams()
  const [currentTab, setTab] = useState('quality')
  const daterange = Timespan.toStaticTimespan(timespan, weekStartsOn)
  const presets = useAnalyticsPresets({
    filters,
    timespan: daterange,
    baseFilters: [
      {
        field: 'handledBy',
        op: 'eq',
        value: memberId,
        dataset: 'interactions',
      },
    ],
  })
  const { data: user } = useQuery(trpc.users.getById.queryOptions({ id: memberId }))

  useTitle(user ? `${user?.firstName} ${user?.lastName}'s Scorecard` : 'Profile')
  return (
    <div className="mt-4 grid gap-4 px-4">
      <header className="flex py-8">
        <h1 className="shrink-0 text-3xl font-bold">
          {user?.firstName} {user?.lastName}
        </h1>
        <div className="flex shrink grow items-center justify-end gap-2">
          <DateRangePicker
            value={timespan}
            onValueChange={(value) => {
              setTimespanPreference(value)
              navigate({
                search: (prev) => ({
                  ...prev,
                  timespan: value,
                }),
              })
            }}
          />

          <CommandPopover
            filterField="channelId"
            label="Channels"
            collection={FilterCollection.Channels}
            selected={filters.channelIds?.values || []}
          >
            <ChannelsCommandList
              multiselect
              onSelect={(ids) =>
                handleFilterValueChange({
                  field: 'channelIds',
                  values: ids,
                })
              }
            />
          </CommandPopover>

          <CommandPopover
            filterField="scorecardId"
            collection={FilterCollection.Scorecards}
            label="Scorecards"
            selected={filters.scorecardIds?.values || []}
          >
            <ScorecardsCommandList
              multiselect
              onSelect={(ids) =>
                handleFilterValueChange({
                  field: 'scorecardIds',
                  values: ids,
                })
              }
            />
          </CommandPopover>
        </div>
      </header>
      <div className="rounded border">
        <div className="bg-accent grid grid-cols-5">
          <div
            className={cn('border-r border-b', {
              'bg-background border-b-background': currentTab === 'quality',
            })}
          >
            <AvgQualityScoreTab
              timespan={presets.timespan}
              filters={presets.filters}
              onClick={() => setTab('quality')}
            />
          </div>
          <div
            className={cn('border-r border-b', {
              'bg-background border-b-background': currentTab === 'sentiment',
            })}
          >
            <AvgSentimentInfluence
              onClick={() => setTab('sentiment')}
              timespan={presets.timespan}
              filters={presets.filters}
            />
          </div>

          <InteractionTabs
            onClick={setTab}
            currentTab={currentTab}
            timespan={presets.timespan}
            filters={presets.filters}
          />
        </div>
        <div className="shrink grow pr-0">{renderTab()}</div>
      </div>
    </div>
  )

  function renderTab() {
    switch (currentTab) {
      case 'quality':
        return (
          <>
            <div className="grid grid-cols-4 gap-8">
              <div className="p-4">
                <h1 className="mt-4 mb-4 text-3xl font-semibold">Quality Score</h1>
                <QualityHeading
                  memberId={memberId}
                  filters={presets.filters}
                  timespan={presets.timespan}
                />
                <div className="h-8" />

                <DrilldownButton
                  filters={[
                    ...presets.filters.map((f) => {
                      if (f.field === 'handledBy') {
                        return { ...f, field: 'handledByIds' as const }
                      }
                      return f
                    }),
                    {
                      field: 'averageEvaluationScore' as const,
                      op: 'isNotNull',
                      dataset: 'interactions',
                    },
                  ]}
                  timespan={presets.timespan as Timespan.StaticTimespan}
                >
                  View interactions
                  <Icon name="arrow-right" className="ml-2" />
                </DrilldownButton>
              </div>
              <div className="col-span-3 border-l">
                <EvaluationScoreTrends
                  filters={presets.filters}
                  timespan={presets.timespan}
                />
              </div>
            </div>
            <TopQualityRanking
              memberId={memberId}
              filters={presets.filters}
              timespan={presets.timespan}
            />
            <EvaluationCriteriaInsights
              memberId={memberId}
              channelIds={filters.channelIds ? filters.channelIds.values : []}
              scorecardIds={filters.scorecardIds ? filters.scorecardIds.values : []}
            />
          </>
        )
      case 'sentiment':
        return (
          <>
            <div className="mt-4 grid grid-cols-3 gap-8 p-4">
              <div>
                <h1 className="mb-4 text-3xl font-semibold">Customer Sentiment</h1>
                <SentimentHeading filters={presets.filters} timespan={presets.timespan} />
              </div>
              <div className="col-span-2">
                <SentimentTrends filters={presets.filters} timespan={presets.timespan} />
              </div>
            </div>
            {/*<SentimentReactions filters={presets.filters} timespan={presets.timespan} />*/}
            <SentimentBreakdown filters={presets.filters} timespan={presets.timespan} />
          </>
        )
      case 'handleTime':
        return (
          <>
            <div className="mt-4 grid grid-cols-3 gap-8 p-4">
              <div>
                <h1 className="mb-4 text-3xl font-semibold">Handle Time</h1>
                <HandleTimeHeading
                  filters={presets.filters}
                  timespan={presets.timespan}
                />
              </div>
              <div className="col-span-2">
                <GenericInteractionTrends
                  alias="Average Handle Time"
                  field="handleTime"
                  format="duration"
                  filters={presets.filters}
                  timespan={presets.timespan}
                />
              </div>
            </div>
            <GenericInteractionBreakdown
              filters={presets.filters}
              timespan={presets.timespan}
              columns={[
                { header: 'AHT', op: 'avg', field: 'handleTime', format: 'duration' },
              ]}
            />
          </>
        )

      case 'deadAir':
        return (
          <>
            <div className="mt-4 grid grid-cols-3 gap-8 p-4">
              <div>
                <h1 className="mb-4 text-3xl font-semibold">Dead Air</h1>
                <DeadAirHeading filters={presets.filters} timespan={presets.timespan} />
              </div>
              <div className="col-span-2">
                <GenericInteractionTrends
                  alias="Average Dead Air"
                  field="totalDeadAirTime"
                  unit="s"
                  filters={presets.filters}
                  timespan={presets.timespan}
                />
              </div>
            </div>
            <GenericInteractionBreakdown
              filters={presets.filters}
              timespan={presets.timespan}
              columns={[
                {
                  header: 'Avg Dead Air',
                  op: 'avg',
                  field: 'totalDeadAirTime',
                  format: 'seconds',
                },
                {
                  header: 'Avg Occurrences',
                  op: 'avg',
                  field: 'deadAirCount',
                  format: 'number',
                },
              ]}
            />
          </>
        )

      case 'overtalk':
        return (
          <>
            <div className="mt-4 grid grid-cols-3 gap-8 p-4">
              <div>
                <h1 className="mb-4 text-3xl font-semibold">Overtalk</h1>
                <OvertalkHeading filters={presets.filters} timespan={presets.timespan} />
              </div>
              <div className="col-span-2">
                <GenericInteractionTrends
                  alias="Avg Overtalk Occurrences"
                  filters={presets.filters}
                  timespan={presets.timespan}
                  field="agentOvertalkOccurrences"
                />
              </div>
            </div>
            <GenericInteractionBreakdown
              filters={presets.filters}
              timespan={presets.timespan}
              columns={[
                {
                  header: 'Avg Overtalk',
                  op: 'avg',
                  field: 'agentOvertalkOccurrences',
                  format: 'number',
                },
              ]}
            />
          </>
        )
    }
  }

  function handleFilterValueChange(opts: {
    field: keyof MetricFilters
    values: string[]
  }) {
    navigate({
      search: (prev: SearchParams) => {
        function compileFilters() {
          return {
            ...prev.filters,
            [opts.field]:
              opts.values.length > 0 ? { op: 'in', values: opts.values } : undefined,
          }
        }

        return {
          ...prev,
          filters: compileFilters(),
        }
      },
    })
  }
}

function AvgQualityScoreTab(props: { onClick: () => void } & BaseReportProps) {
  const { data } = useAnalytics({
    filters: [...props.filters, { field: 'hasEvaluation', op: 'eq', value: true }],
    timespan: props.timespan,
    dataset: 'interactions',
    aggregations: {
      avg: {
        op: 'avg',
        field: 'averageEvaluationScore',
      },
    },
  })
  const firstRow = data?.[0]
  return (
    <IndicatorTab
      label="Quality Score"
      value={toPercent(firstRow?.avg)}
      onClick={props.onClick}
    />
  )
}

function QualityHeading(props: { memberId: string } & BaseReportProps) {
  const { data } = useAnalytics({
    filters: props.filters,
    timespan: props.timespan,
    dataset: 'interactions',
    aggregations: {
      avg: {
        op: 'avg',
        field: 'averageEvaluationScore',
      },
    },
  })
  const firstRow = data?.[0]

  const rankingFilters = [
    ...props.filters.filter((filter) => filter.field !== 'handledBy'),
    {
      field: 'averageEvaluationScore',
      op: 'isNotNull',
      dataset: 'interactions',
    } as const,
  ]

  const { data: rankingData } = useAnalytics({
    filters: rankingFilters,
    timespan: props.timespan,
    dataset: 'interactions',
    aggregations: {
      handledBy: {
        field: 'handledBy',
        hydrate: true,
        dataset: 'interactions',
      },
      avgQualityScore: {
        op: 'avg',
        field: 'averageEvaluationScore',
      },
    },
    orderBy: [{ alias: 'avgQualityScore', direction: 'desc' }],
  })

  const rankedRows = (rankingData || [])
    .map((row, index) => ({
      rank: index + 1,
      handledById: (row.handledBy as unknown as { id: string })?.id,
    }))
    .filter((row) => row.handledById)

  const memberRank = rankedRows.find((row) => row.handledById === props.memberId)?.rank

  // Calculate previous period for trends
  const daysInRange = differenceInDays(props.timespan.end, props.timespan.start)
  const previousPeriodStart = subDays(props.timespan.start, daysInRange)
  const previousPeriodEnd = subDays(props.timespan.end, daysInRange)

  const { data: previousData } = useAnalytics({
    filters: props.filters,
    timespan: { start: previousPeriodStart, end: previousPeriodEnd },
    dataset: 'interactions',
    aggregations: {
      avg: {
        op: 'avg',
        field: 'averageEvaluationScore',
      },
    },
  })

  const { data: previousRankingData } = useAnalytics({
    filters: rankingFilters,
    timespan: { start: previousPeriodStart, end: previousPeriodEnd },
    dataset: 'interactions',
    aggregations: {
      handledBy: {
        field: 'handledBy',
        hydrate: true,
        dataset: 'interactions',
      },
      avgQualityScore: {
        op: 'avg',
        field: 'averageEvaluationScore',
      },
    },
    orderBy: [{ alias: 'avgQualityScore', direction: 'desc' }],
  })

  const previousRankedRows = (previousRankingData || [])
    .map((row, index) => ({
      rank: index + 1,
      handledById: (row.handledBy as unknown as { id: string })?.id,
    }))
    .filter((row) => row.handledById)

  const previousMemberRank = previousRankedRows.find(
    (row) => row.handledById === props.memberId,
  )?.rank

  const currentScore = parseFloat(firstRow?.avg || '0')
  const previousScore = parseFloat(previousData?.[0]?.avg || '0')

  // Calculate trends
  const scoreTrend: 'up' | 'down' | 'neutral' =
    currentScore > previousScore
      ? 'up'
      : currentScore < previousScore
        ? 'down'
        : 'neutral'

  const rankTrend: 'up' | 'down' | 'neutral' =
    memberRank && previousMemberRank
      ? memberRank < previousMemberRank
        ? 'up'
        : memberRank > previousMemberRank
          ? 'down'
          : 'neutral'
      : 'neutral'

  return (
    <div className="flex gap-3">
      <IndicatorTab
        className="max-w-[160px] grow border-0 p-0"
        value={toPercent(firstRow?.avg)}
        label="Quality Score"
        trend={scoreTrend}
      />
      <IndicatorTab
        className="max-w-[160px] grow border-0 p-0"
        value={memberRank ? `#${memberRank}` : '—'}
        label="Ranking"
        trend={rankTrend}
      />
    </div>
  )
}

function AvgSentimentInfluence(props: { onClick: () => void } & BaseReportProps) {
  const { data } = useAnalytics({
    filters: props.filters,
    timespan: props.timespan,
    dataset: 'interactions',
    aggregations: {
      avg: {
        op: 'avg',
        field: 'sentimentInfluence',
      },
    },
  })
  const firstRow = data?.[0]
  return (
    <IndicatorTab
      label="Sentiment Influence"
      value={toPercent(firstRow?.avg)}
      onClick={props.onClick}
    />
  )
}

function TopQualityRanking(props: { memberId: string } & BaseReportProps) {
  type RankedAgent = {
    id: string
    firstName: string
    lastName: string
    avatarFileId?: string | null
  }

  const rankingFilters = [
    ...props.filters.filter((filter) => filter.field !== 'handledBy'),
    {
      field: 'averageEvaluationScore',
      op: 'isNotNull',
      dataset: 'interactions',
    } as const,
  ]

  const { data, loading } = useAnalytics({
    filters: rankingFilters,
    timespan: props.timespan,
    dataset: 'interactions',
    aggregations: {
      handledBy: {
        field: 'handledBy',
        hydrate: true,
        dataset: 'interactions',
      },
      avgQualityScore: {
        op: 'avg',
        field: 'averageEvaluationScore',
      },
      interactionCount: {
        op: 'count',
        field: 'id',
      },
    },
    orderBy: [{ alias: 'avgQualityScore', direction: 'desc' }],
  })

  const rankedRows = (data || [])
    .map((row, index) => ({
      rank: index + 1,
      handledBy: row.handledBy as unknown as RankedAgent,
      avgQualityScore: parseFloat(row.avgQualityScore),
      interactionCount: parseInt(row.interactionCount),
    }))
    .filter((row) => row.handledBy?.id)

  const topTen = rankedRows.slice(0, 10)
  const memberRow = rankedRows.find((row) => row.handledBy?.id === props.memberId)
  const shouldAppendMemberRow = !!memberRow && memberRow.rank > 10
  const showGapRow = !!memberRow && memberRow.rank > topTen.length + 1

  return (
    <div className="border-t">
      <div className="border-b p-4">
        <h2 className="text-lg font-semibold">Top Agents by Avg Quality Score</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full table-auto text-sm">
          <thead>
            <tr className="bg-accent/40 border-b text-left">
              <th className="w-16 p-3 font-semibold">Rank</th>
              <th className="p-3 font-semibold">Agent</th>
              <th className="w-28 p-3 text-right font-semibold">Avg QS</th>
              <th className="w-36 p-3 text-right font-semibold">Interactions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && topTen.length === 0 && (
              <tr>
                <td colSpan={4} className="text-muted-foreground p-4 text-center">
                  No ranking data for this time range.
                </td>
              </tr>
            )}

            {topTen.map((row) => {
              const isCurrentMember = row.handledBy.id === props.memberId

              return (
                <tr
                  key={row.handledBy.id}
                  className={cn('border-b', {
                    'bg-yellow-300/15': isCurrentMember,
                  })}
                >
                  <td className="p-3 font-mono">#{row.rank}</td>
                  <td className="p-3">
                    <Link
                      to="/$alias/members/$memberId"
                      params={{ memberId: row.handledBy.id }}
                      from="/$alias"
                    >
                      <span className="flex items-center gap-3">
                        <Avatar {...row.handledBy} />
                        <span className="font-medium">
                          {row.handledBy.firstName} {row.handledBy.lastName}
                        </span>
                      </span>
                    </Link>
                  </td>
                  <td className="p-3 text-right font-mono">
                    {toPercent(row.avgQualityScore)}
                  </td>
                  <td className="p-3 text-right font-mono">
                    {commafy(row.interactionCount, 0)}
                  </td>
                </tr>
              )
            })}

            {shouldAppendMemberRow && memberRow && showGapRow && (
              <tr>
                <td
                  colSpan={4}
                  className="text-muted-foreground bg-accent border-b p-2 text-center font-mono"
                >
                  ...
                </td>
              </tr>
            )}

            {shouldAppendMemberRow && memberRow && (
              <tr className="border-b bg-yellow-300/15">
                <td className="p-3 font-mono">#{memberRow.rank}</td>
                <td className="p-3">
                  <Link
                    to="/$alias/members/$memberId"
                    params={{ memberId: memberRow.handledBy.id }}
                    from="/$alias"
                  >
                    <span className="flex items-center gap-3">
                      <Avatar {...memberRow.handledBy} />
                      <span className="font-medium">
                        {memberRow.handledBy.firstName} {memberRow.handledBy.lastName}
                      </span>
                    </span>
                  </Link>
                </td>
                <td className="p-3 text-right font-mono">
                  {toPercent(memberRow.avgQualityScore)}
                </td>
                <td className="p-3 text-right font-mono">
                  {commafy(memberRow.interactionCount, 0)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function InteractionTabs(
  props: { onClick: (tab: string) => void; currentTab: string } & BaseReportProps,
) {
  const trpc = useTRPC()
  const { data: org } = useQuery(trpc.organizations.getThresholds.queryOptions())
  const { data } = useAnalytics({
    filters: props.filters,
    timespan: props.timespan,
    dataset: 'interactions',
    aggregations: {
      aht: {
        op: 'avg',
        field: 'handleTime',
      },
      ada: {
        op: 'avg',
        field: 'totalDeadAirTime',
      },
      aot: {
        op: 'avg',
        field: 'agentOvertalkOccurrences',
      },
    },
  })
  const firstRow = data?.[0]

  return firstRow ? (
    <>
      <div
        className={cn('border-r border-b', {
          'bg-background border-b-background': props.currentTab === 'handleTime',
        })}
      >
        <IndicatorTab
          label="Handle Time"
          onClick={() => props.onClick('handleTime')}
          value={formatDuration(firstRow?.aht)}
          percentToGoal={
            (parseInt(firstRow?.aht) <
              (org?.thresholds?.phone?.handleTime ?? AVERAGE_HANDLE_TIME_THRESHOLD)) +
            '%'
          }
        />
      </div>
      <div
        className={cn('border-r border-b', {
          'bg-background border-b-background': props.currentTab === 'deadAir',
        })}
      >
        <IndicatorTab
          label="Dead Air"
          onClick={() => props.onClick('deadAir')}
          value={`${parseInt(firstRow?.ada)}s`}
          percentToGoal={
            (parseInt(firstRow?.ada) <
              (org?.thresholds?.phone?.deadAir ?? DEAD_AIR_THRESHOLD_PHONE)) +
            '%'
          }
        />
      </div>
      <div
        className={cn('border-b', {
          'bg-background border-b-background': props.currentTab === 'overtalk',
        })}
      >
        <IndicatorTab
          label="Overtalk Occurrences"
          onClick={() => props.onClick('overtalk')}
          percentToGoal={calculateOvertalkScore(firstRow?.aot) + '%'}
          value={commafy(firstRow?.aot, 1)}
        />
      </div>
    </>
  ) : null
}
